'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SelectProps } from './types';
import { ChevronDown, Close } from '@1debit/icons';
import { cn } from 'utils/styles';

const HIDDEN_CLASS = 'hidden';
const SELECTED_CLASS = 'selected';
const SELECT_WRAPPER_CLASS = 'select-wrapper';
const SELECT_LIST_ITEM_CLASS = 'select-list-item';
const OPTION_SELECT_CLASS = 'option-select';

const toggleDropdownSelected = (wrapper: HTMLElement, selectedColumn: string) => {
  wrapper.querySelectorAll(`.${SELECT_LIST_ITEM_CLASS}`).forEach(option => {
    const isSelected = (option as HTMLElement).dataset.columns === selectedColumn;
    option.classList.toggle(SELECTED_CLASS, isSelected);
    option.classList.toggle(HIDDEN_CLASS, !isSelected);
    option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
  });
};

interface OptionPanelProps {
  selectOptions: SelectProps['selectOptions'];
  optionHeader: SelectProps['optionHeader'];
  colsToSkip: number;
  onClose: () => void;
  onSelect: (columnIndex: number) => void;
}

const OptionPanel = ({ selectOptions, optionHeader, colsToSkip, onClose, onSelect }: OptionPanelProps) => (
  <div className={`${OPTION_SELECT_CLASS} fixed bg-shade-dark top-0 left-0 w-full h-full z-110`}>
    <div className="fixed bottom-0 left-0 bg-primary text-kale w-full rounded-tr-xl rounded-tl-xl py-6 px-4">
      <Close className="float-right" role="button" onClick={onClose} />
      {optionHeader && <div className="font-sans font-semibold text-heading-sm mb-3">{optionHeader}</div>}
      {selectOptions?.map((option, i) => (
        <div
          key={option.key}
          data-columns={i + colsToSkip}
          className={cn('py-6 border-shade', i + 1 < selectOptions.length && 'border-b')}
          onClick={() => onSelect(i + colsToSkip)}>
          <span className="pointer-events-none">{option.key}</span>
        </div>
      ))}
    </div>
  </div>
);

const Select = ({ selectOptions, onOptionClick, optionHeader, colsToSkip = 0, bordered = false }: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  const toggleOptionSelect = () => setOpen(prev => !prev);

  const onOptionSelected = (columnIndex: number) => {
    if (wrapperRef.current) {
      toggleDropdownSelected(wrapperRef.current, String(columnIndex));
      onOptionClick?.(columnIndex, wrapperRef.current);
    }
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={cn(SELECT_WRAPPER_CLASS, bordered ? 'flex w-full' : 'inline-flex')}>
      <div
        role="listbox"
        className={cn(
          'flex items-center cursor-pointer focus:outline-0 focus:shadow-none',
          bordered
            ? 'justify-between w-full border border-(--keyline-color) rounded-md h-10 px-4 py-2 text-label-saans font-medium'
            : 'border-b border-(--keyline-color) mx-1',
        )}
        onClick={toggleOptionSelect}>
        <div className={cn(!bordered && 'border-0 p-0 pr-1', 'flex items-center')}>
          {selectOptions?.map((option, i) => (
            <div
              key={option.key}
              role="option"
              className={`${SELECT_LIST_ITEM_CLASS} cursor-pointer ${i === 0 ? SELECTED_CLASS : HIDDEN_CLASS}`}
              data-columns={i + colsToSkip}
              aria-selected={i === 0}>
              {option.key}
            </div>
          ))}
        </div>
        <ChevronDown className="shrink-0" />
      </div>
      {mounted &&
        open &&
        createPortal(
          <OptionPanel
            selectOptions={selectOptions}
            optionHeader={optionHeader}
            colsToSkip={colsToSkip}
            onClose={toggleOptionSelect}
            onSelect={onOptionSelected}
          />,
          document.body,
        )}
    </div>
  );
};

export default Select;
